import {observable, computed, action, autorun} from 'mobx';
import io from 'socket.io-client';
const md5 = require('blueimp-md5');

const location = typeof window === 'object' ? window.location.origin : process.ENV.BASE_URL;
const socket = io(location);

export default class PersonaStore {
  constructor(rootStore){
    this._rootStore = rootStore;
    // Let the websocket fun begin
    if (!this.inIframe) {
      this.openStream();
    }
  }

  // No websocket so use normal updates
  socketConnected = false;
  @observable count = 0;

  @observable id = null;
  @observable key = null;

  personaHash = null;
  @observable updated_at = null;
  created_at = null;
  // Persona Body
  @observable body = {};

  // This fires off our update or create actions when the body has been updated
  bodyWatcher = autorun(() => {
    const bodyIsPopulated = Object.keys(this.body).length > 0;
    if (bodyIsPopulated && !this.id) {
      this.createPersona();
      return;
    }
    if (bodyIsPopulated && this.key && this.isChanged) {
      this.updatePersona();
      return;
    }
  })

  // Holds the state for if we are in iframe
  @computed
  get inIframe() {
    if(!window) return false;
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  // Checks to see if the persona has changed since it was last updated
  @computed
  get isChanged() {
    const newHash = md5(JSON.stringify(this.body));
    if (newHash === this.personaHash){
      return false;
    } else {
      return true;
    }
  }

  @computed
  get isHome() {
    const {location} = this._rootStore.routerStore;
    return location.pathname === '/';
  }

  @computed
  get isEditable() {
    let editable = false;
    if (this.isHome){
      editable = true;
    }
    if (this.key){
      editable = true;
    }
    return editable;
  }

  @action.bound
  updateBody(body) {
    this.body = body;
  }

  @action.bound
  resetPersona() {
    socket.emit('leave', this.id);
    this.body = {};
    this.id = null;
    this.key = null;
    this.updated_at = null;
    this.created_at = null;
    this.personaHash = null;
  }

  @action.bound
  async getPersona(id=this.id, key=this.key) {
    try {
      const response = await fetch(
        '/api/' + id,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
      if (response.status !== 200) {
        this._rootStore.alertStore.addError(response.error);
      } else {
        const json = await response.json();
        if (key) {
          json.data.key = key;
        }
        this.receiveData(json.data);
      }
    } catch (err) {
      this._rootStore.alertStore.addError(err);
    }
  }

  @action.bound
  async updatePersona() {
    const id = this.id,
          key=this.key;
    if (this.socketConnected) {
      socket.emit('updatePersona', {
        id: this.id,
        key: this.key,
        persona: this.body,
      });
    } else {
      try {
        const response = await fetch(
          '/api/' + id + '/' + key,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({persona: this.body})
          });
        if (response.status !== 200) {
          this._rootStore.alertStore.addError(response.error);
        } else {
          const json = await response.json();
          json.data.key = key;
          this.receiveData(json.data);
        }
      } catch (err) {
        this._rootStore.alertStore.addError(err);
      }
    }
  }

  @action.bound
  async createPersona() {
    const template_id = this._rootStore.templateStore.selectedTemplate._id;
    const persona = Object.assign({template_id: template_id}, this.body);
    if (this.socketConnected) {
      socket.emit('createPersona', {
        persona,
      });
    } else {
      const response = await fetch(
        '/api/new',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({persona})
        }
      );
      const json = await response.json();

      this.receiveData(json.data);
      // Redirect to newly created persona
      this._rootStore.routerStore.push('/' + this.id + '/' + this.key);
    }
    this._rootStore.alertStore.dismissAllAlerts();
    this._rootStore.alertStore.addCreateAlert();
    this._rootStore.templateStore.hideTemplateSelector();
  }

  @action.bound
  receiveData(data) {
    this.body = data.persona;
    this.id = data.id;
    if (data.key) {
      this.key = data.key;
    }
    this.updated_at = data.updated_at;
    this.created_at = data.created_at;
    this.personaHash = md5(JSON.stringify(data.persona));
    this._rootStore.sidebarStore.updatePersonaList();
  }

  @action.bound
  receiveSocketData(data) {
    this.updated_at = data.updated_at;
    if (data.key) {
      this.key = data.key;
    }
    if (data.id) {
      this.id = data.id;
    }
    if (data.created_at) {
      this.created_at = data.created_at;
    }
    if (data.difference && Object.keys(data.difference).length > 0) {
      this.body = Object.assign({}, this.body, data.difference);
    }
    this.personaHash = md5(JSON.stringify(this.body));
    this._rootStore.sidebarStore.updatePersonaList();
  }

  openStream = () => {
    // Should hopefully just fire once
    socket.on('connect', () => {
      this.socketConnected = true;
      console.log('websocket connected');
      if (this.id) {
        console.log('Joining:', this.id);
        socket.emit('join', this.id);
      }
    });

    socket.on('disconnect', () => {
      this.socketConnected = false;
    });

    socket.on('joined', (data) => {
      this.count = data.count;
    });

    socket.on('createdPersona', (data) => {
      this.receiveSocketData(data);
      this._rootStore.routerStore.push('/' + this.id + '/' + this.key);
    });

    socket.on('updatedPersona', (data) => {
      if (data.count) {
        this.count = data.count;
      }
      this.receiveSocketData(data);
    });

    socket.on('disconnect', (reason) => {
      this.socketConnected = false;
      console.log( 'websocket disconnected', reason);
    });

  }

  closeStream = () => {
    socket.close();
  }

}



// WEBPACK FOOTER //
// ./client/modules/PersonaStore.js